<template>
  <div>
    <div class="card-header py-3">
      {{ $t("guest_hub.header") }}
    </div>
    <div class="card-body mt-2">
      <form @submit.prevent>
        <div v-if="success" class="alert alert-success" role="alert">
          {{ $t("guest_hub.success_msg") }}
        </div>
        <div v-if="error_msg" class="alert alert-danger" role="alert">
          {{ error_msg }}
        </div>

        <!-- Setup steps -->
        <div class="justify-content-left mb-4">
          <p class="mb-1">{{ $t("guest_hub.steps_title") }}</p>
          <ul class="mb-0 pl-4">
            <li>{{ $t("guest_hub.step_1") }}</li>
            <li>{{ $t("guest_hub.step_2") }}</li>
            <li>{{ $t("guest_hub.step_3") }}</li>
            <li>{{ $t("guest_hub.step_4") }}</li>
          </ul>
        </div>

        <!-- Quick Check-in / Check-out dependency warnings -->
        <div v-if="showQuickCheckinWarning || showQuickCheckoutWarning" class="alert alert-warning" role="alert">
          <div v-if="showQuickCheckinWarning">{{ $t("guest_hub.quick_checkin_warning") }}</div>
          <div v-if="showQuickCheckoutWarning">{{ $t("guest_hub.quick_checkout_warning") }}</div>
          <router-link to="/dashboard/quick-checkout-in-setting">
            {{ $t("guest_hub.quick_setting_link") }}
          </router-link>
        </div>

        <!-- Mode selects -->
        <div class="row mb-4">
          <div class="col-md-6">
            <label class="font-weight-bold">{{ $t("guest_hub.checkin_label") }}</label>
            <select v-model.number="guest_hub_setting.value.checkin_mode" class="form-control w-75">
              <option
                v-for="option in checkinModeOptions"
                :key="`ci-${option.value}`"
                :value="option.value"
              >{{ option.label }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="font-weight-bold">{{ $t("guest_hub.checkout_label") }}</label>
            <select v-model.number="guest_hub_setting.value.checkout_mode" class="form-control w-75">
              <option
                v-for="option in checkoutModeOptions"
                :key="`co-${option.value}`"
                :value="option.value"
              >{{ option.label }}</option>
            </select>
          </div>
        </div>

        <!-- Operating time validation -->
        <div v-if="time_error" class="alert alert-danger" role="alert">
          {{ time_error }}
        </div>

        <!-- Operating times (grayed out when the side is Not in USE) -->
        <div class="row mb-4">
          <div class="col-md-6" :class="{ 'disabled-block': !checkinInUse }">
            <label class="font-weight-bold">{{ $t("guest_hub.checkin_operating_time") }}</label>
            <div class="form-group row align-items-center mb-2">
              <div class="col-3">{{ $t("guest_hub.start") }}</div>
              <div class="col-6">
                <select v-model="guest_hub_setting.value.operating_hours.checkin.start" class="form-control" :disabled="!checkinInUse">
                  <option value="">--:--</option>
                  <option v-for="time in timeOptions" :key="`cis-${time}`" :value="time">{{ time }}</option>
                </select>
              </div>
            </div>
            <div class="form-group row align-items-center">
              <div class="col-3">{{ $t("guest_hub.finish") }}</div>
              <div class="col-6">
                <select v-model="guest_hub_setting.value.operating_hours.checkin.end" class="form-control" :disabled="!checkinInUse">
                  <option value="">--:--</option>
                  <option v-for="time in timeOptions" :key="`cie-${time}`" :value="time">{{ time }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="col-md-6" :class="{ 'disabled-block': !checkoutInUse }">
            <label class="font-weight-bold">{{ $t("guest_hub.checkout_operating_time") }}</label>
            <div class="form-group row align-items-center mb-2">
              <div class="col-3">{{ $t("guest_hub.start") }}</div>
              <div class="col-6">
                <select v-model="guest_hub_setting.value.operating_hours.checkout.start" class="form-control" :disabled="!checkoutInUse">
                  <option value="">--:--</option>
                  <option v-for="time in timeOptions" :key="`cos-${time}`" :value="time">{{ time }}</option>
                </select>
              </div>
            </div>
            <div class="form-group row align-items-center">
              <div class="col-3">{{ $t("guest_hub.finish") }}</div>
              <div class="col-6">
                <select v-model="guest_hub_setting.value.operating_hours.checkout.end" class="form-control" :disabled="!checkoutInUse">
                  <option value="">--:--</option>
                  <option v-for="time in timeOptions" :key="`coe-${time}`" :value="time">{{ time }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- QR / URL: one code for the whole Guest Hub. The kiosk shows
             check-in or check-out by itself, from the operating times above. -->
        <div class="row mb-4">
          <div class="col-md-6" :class="{ 'disabled-block': !anyInUse }">
            <label class="font-weight-bold">{{ $t("guest_hub.qr_url") }}</label>
            <div class="d-flex align-items-center">
              <qrcode-vue :value="kioskUrl" :size="120" level="M" />
              <div class="ml-4">
                <a
                  :href="anyInUse ? kioskUrl : null"
                  target="_blank"
                  class="btn btn-default border d-block mb-2"
                  :class="{ disabled: !anyInUse }"
                >{{ $t("guest_hub.open") }}</a>
                <button
                  type="button"
                  class="btn btn-default border d-block w-100"
                  :disabled="!anyInUse"
                  @click="copy(kioskUrl)"
                >{{ copied ? $t("guest_hub.copied") : $t("guest_hub.copy") }}</button>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-12 mt-4" align="center">
            <button
              @click.prevent="$router.push({ name: 'checkin-facility-dashboard-home' })"
              class="btn btn-danger ml-3"
            >
              {{ $t("guest_hub.back") }}
            </button>
            <button
              class="btn-primary btn ml-3"
              @click.prevent="confirm"
              type="button"
            >
              {{ $t("guest_hub.confirm") }}
            </button>
          </div>
        </div>
      </form>
    </div>

    <confirmation-modal-component @click-handler="submit"></confirmation-modal-component>
  </div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import ConfirmationModalComponent from "../components/confirmation-modal.component";
import {
  GUEST_HUB_SETTING_NAME,
  KIOSK_CHECKIN_MODES,
  KIOSK_CHECKOUT_MODES,
} from "../../checkin/kiosk/kiosk_config";

export default {
  components: {
    ConfirmationModalComponent,
    QrcodeVue,
  },
  data() {
    return {
      facility: {},
      guest_hub_setting: {
        facility_id: 0,
        name: GUEST_HUB_SETTING_NAME,
        value: {
          checkin_mode: KIOSK_CHECKIN_MODES.NOT_IN_USE,
          checkout_mode: KIOSK_CHECKOUT_MODES.NOT_IN_USE,
          operating_hours: {
            checkin: { start: "", end: "" },
            checkout: { start: "", end: "" },
          },
        },
      },
      quick_checkin_on: true,
      quick_checkout_on: true,
      success: false,
      error_msg: "",
      copied: false,
      kioskUrl: "",
    };
  },
  computed: {
    checkinModeOptions() {
      return [
        { value: KIOSK_CHECKIN_MODES.NOT_IN_USE, label: this.$t("guest_hub.mode_not_in_use") },
        { value: KIOSK_CHECKIN_MODES.BOTH, label: this.$t("guest_hub.mode_confirm_pre_start") },
        { value: KIOSK_CHECKIN_MODES.PRE_CHECKIN_ONLY, label: this.$t("guest_hub.mode_confirm_pre_only") },
        { value: KIOSK_CHECKIN_MODES.START_CHECKIN_ONLY, label: this.$t("guest_hub.mode_start_checkin_only") },
        { value: KIOSK_CHECKIN_MODES.START_PRE_CHECKIN_ONLY, label: this.$t("guest_hub.mode_start_pre_checkin_only") },
      ];
    },
    checkoutModeOptions() {
      return [
        { value: KIOSK_CHECKOUT_MODES.NOT_IN_USE, label: this.$t("guest_hub.mode_not_in_use") },
        { value: KIOSK_CHECKOUT_MODES.START_CHECKOUT, label: this.$t("guest_hub.mode_start_checkout") },
      ];
    },
    // "HH:mm" options in 30-minute steps
    timeOptions() {
      const options = [];
      for (let h = 0; h < 24; h++) {
        for (const m of ["00", "30"]) {
          options.push(`${String(h).padStart(2, "0")}:${m}`);
        }
      }
      return options;
    },
    checkinInUse() {
      return this.guest_hub_setting.value.checkin_mode !== KIOSK_CHECKIN_MODES.NOT_IN_USE;
    },
    checkoutInUse() {
      return this.guest_hub_setting.value.checkout_mode !== KIOSK_CHECKOUT_MODES.NOT_IN_USE;
    },
    // The single kiosk URL / QR code is only worth handing out once at least
    // one side is switched on; with both off the kiosk would say "No Operation".
    anyInUse() {
      return this.checkinInUse || this.checkoutInUse;
    },
    // Operating time validation: each range must be forward in time, and the
    // check-in / check-out ranges must not overlap (a single Guest Hub device
    // cannot be in both modes at the same moment).
    time_error() {
      const hours = this.guest_hub_setting.value.operating_hours;
      const checkin = this.checkinInUse ? this.toRange(hours.checkin) : null;
      const checkout = this.checkoutInUse ? this.toRange(hours.checkout) : null;

      if (checkin && checkin.start >= checkin.end) {
        return this.$t("guest_hub.error_checkin_time_order");
      }
      if (checkout && checkout.start >= checkout.end) {
        return this.$t("guest_hub.error_checkout_time_order");
      }
      if (checkin && checkout && checkin.start < checkout.end && checkout.start < checkin.end) {
        return this.$t("guest_hub.error_time_overlap");
      }
      return "";
    },
    // Every Guest Hub scenario requires the matching Quick switch ON
    showQuickCheckinWarning() {
      return this.checkinInUse && !this.quick_checkin_on;
    },
    showQuickCheckoutWarning() {
      return this.checkoutInUse && !this.quick_checkout_on;
    },
  },
  async mounted() {
    this.facility = window._app_facility;
    this.guest_hub_setting.facility_id = this.facility.id;

    const kioskHome = this.$router.resolve({
      name: "checkin-facility-kiosk-home",
      params: { slug_name: this.facility.slug_name },
    }).href;
    // One URL for the whole Guest Hub, no query: the kiosk picks check-in or
    // check-out from the operating times saved here (see KIOSK_MODES in
    // kiosk_config.js). Codes printed earlier with `?mode=...` still work —
    // the kiosk ignores the param.
    this.kioskUrl = `${window.location.origin}${kioskHome}`;

    await Promise.all([this.loadSetting(), this.loadQuickSettings()]);
  },
  methods: {
    // "HH:mm" pair -> minutes from midnight, or null when incomplete
    toRange({ start, end }) {
      if (!start || !end) return null;
      const toMinutes = (time) => {
        const [h, m] = time.split(":").map(Number);
        return h * 60 + m;
      };
      return { start: toMinutes(start), end: toMinutes(end) };
    },
    async loadSetting() {
      const setting = await this.$lionheart
        .get("/v2/facility-settings", {
          params: {
            name: this.guest_hub_setting.name,
            facility_id: this.guest_hub_setting.facility_id,
            paginate: 0,
          },
        })
        .then((r) => r.data.data);

      if (setting.length == 0) return;

      const value = setting[0].value ?? {};
      const current = this.guest_hub_setting.value;

      this.guest_hub_setting.value = {
        checkin_mode: value.checkin_mode ?? current.checkin_mode,
        checkout_mode: value.checkout_mode ?? current.checkout_mode,
        operating_hours: {
          checkin: { ...current.operating_hours.checkin, ...(value.operating_hours?.checkin ?? {}) },
          checkout: { ...current.operating_hours.checkout, ...(value.operating_hours?.checkout ?? {}) },
        },
      };
    },
    async loadQuickSettings() {
      const load = async (name) => {
        const setting = await this.$lionheart
          .get("/v2/facility-settings", {
            params: { name, facility_id: this.facility.id, paginate: 0 },
          })
          .then((r) => r.data.data);
        return setting.length ? setting[0].value.visibility == true : false;
      };

      this.quick_checkin_on = await load("quick_checkin_setting");
      this.quick_checkout_on = await load("quick_checkout_setting");
    },
    confirm() {
      this.success = false;
      this.error_msg = "";

      const hours = this.guest_hub_setting.value.operating_hours;
      const missing =
        (this.checkinInUse && (!hours.checkin.start || !hours.checkin.end)) ||
        (this.checkoutInUse && (!hours.checkout.start || !hours.checkout.end));

      if (missing) {
        this.error_msg = this.$t("guest_hub.error_time_required");
        return;
      }

      if (this.time_error) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      this.$bvModal.show("modal-confirmation");
    },
    async submit() {
      const res = await this.$lionheart
        .post("/v2/facility-settings", { ...this.guest_hub_setting })
        .then((r) => r.data);

      this.success = res.success == true;
      if (this.success) window.scrollTo({ top: 0, behavior: "smooth" });
    },
    async copy(url) {
      await navigator.clipboard.writeText(url);
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    },
  },
};
</script>

<style scoped>
.disabled-block {
  opacity: 0.5;
  pointer-events: none;
}
.disabled-block select,
.disabled-block .btn {
  pointer-events: none;
}
</style>
